import React from 'react';
import convert from 'htmr';
import {
  Grid,
  Row,
  Column,
  Card,
  Divider,
  Ribbon,
  Carousel,
  Text
} from '@dls/web';
import Spacing from '../Spacing';
import Anchor from '../Anchor';
import { H3, SubtitleSecondary, BodyPrimary, BodySecondary } from '../Base';
import { simOnlyTncUrl } from '../../config/links';
import htmlStringToReactComponent from '../../helpers/htmlStringToReactComponent';
import {
  StyledDescription,
  StyledImg,
  StyledImgPlus,
  StyledImageCaptionContainer,
  StyledImageListContainer,
  StyledBackArrowIcon,
  StyledAnchorText,
  StyledPlanSelectionArrowContainer,
  StyledHeroImage,
  StyledTitle,
  StyledButtonContainer,
  StyledSecondaryButton,
  SecondaryButton,
  StyledPrimaryButton,
  StyledSimOnlyImageCaption,
  StyledDeviceInstallmentPlanImageCaption,
  StyledMainGridContainer,
  StyledFooterContent,
  StyledNameCard,
  StyledPhoneSelectionContainer,
  StyledListItemPlan,
  StyledUnorderedListPlan,
  StyledPlanDescContainer
} from './styles';
import ImageSimOnly from '../../assets/images/exclusive-validation/simonly.jpg';
import ImagePlus from '../../assets/images/exclusive-validation/plus.jpg';
import ImageDevice from '../../assets/images/exclusive-validation/device.jpg';
import ImageSimOnlyCis from '../../assets/images/exclusive-validation/simonlyCis.png';
import ImagePlusCis from '../../assets/images/exclusive-validation/plusCis.png';
import ImageDeviceCis from '../../assets/images/exclusive-validation/deviceCis.png';
import useTranslation from '../../hooks/useTranslation';
import unlimitedTalktimeSms from '../../helpers/unlimited-talktime-sms';
import {
  renderPrice,
  renderPlanTags,
  formatPlanData
} from '../PlanCatalogCard';
import { Tags } from '../Tag';
import { getRibbon, setPriceText } from '../../helpers/catalogue-helpers';
interface SimOnlyPlusUpSellProps {
  phones: any;
  hideSimOnlyPlusUpSellScreen: () => void;
  showEligibleDevicesForSimOnlyPlus: () => void;
  proceedSimOnlyPlusWithoutDeviceFlow: () => void;
  navigateProductDetailsPage: (phone: any) => void;
  selectedPlanGroup: any;
  selectedPlan: any;
  topChoicesLimit: number;
  isCis: boolean;
}

const SimOnlyPlusUpSell = ({
  phones,
  hideSimOnlyPlusUpSellScreen,
  showEligibleDevicesForSimOnlyPlus,
  proceedSimOnlyPlusWithoutDeviceFlow,
  navigateProductDetailsPage,
  selectedPlanGroup,
  selectedPlan,
  topChoicesLimit,
  isCis
}: SimOnlyPlusUpSellProps) => {
  const { t } = useTranslation(null);
  const topChoicesPhones =
    phones.length > topChoicesLimit ? phones.slice(0, topChoicesLimit) : phones;

  return (
    <StyledMainGridContainer fluid>
      <Row>
        <StyledPlanSelectionArrowContainer xs={12} sm={4} md={4} noGutter>
          <Spacing bottom={1.5}>
            <Anchor onClick={hideSimOnlyPlusUpSellScreen}>
              <StyledBackArrowIcon color="currentColor" size={14} />
              <StyledAnchorText>{t('PLAN_SELECTION')}</StyledAnchorText>
            </Anchor>
          </Spacing>
          {selectedPlan && (
            <Spacing bottom={5}>
              <Card shadow="sm" radius="sm" padding="sm">
                <Card.Content>
                  <StyledNameCard>{selectedPlan?.planName}</StyledNameCard>
                  <Spacing
                    bottom={1.5}
                    css={{ display: 'inline-flex', alignItems: 'center' }}
                  >
                    <Text type="sectionTitle02">
                      {selectedPlan?.data?.quantity &&
                        formatPlanData(
                          selectedPlan.data.quantity,
                          selectedPlan.data.unit
                        )}
                    </Text>
                    {selectedPlan?.tags && (
                      <Tags css={{ marginLeft: 8 }}>
                        {renderPlanTags(selectedPlan.tags)}
                      </Tags>
                    )}
                  </Spacing>
                  <Spacing bottom={2}>
                    <StyledPlanDescContainer>
                      <BodySecondary as="div">
                        <StyledUnorderedListPlan>
                          {selectedPlan?.talktime && selectedPlan?.sms && (
                            <StyledListItemPlan>
                              {unlimitedTalktimeSms({
                                talktime: selectedPlan.talktime,
                                sms: selectedPlan.sms
                              })}
                            </StyledListItemPlan>
                          )}
                        </StyledUnorderedListPlan>
                        {selectedPlan?.planDescSubLiner && (
                          <StyledUnorderedListPlan>
                            {convert(`${selectedPlan.planDescSubLiner}`, {
                              transform: {
                                a: Anchor,
                                li: StyledListItemPlan
                              }
                            })}
                          </StyledUnorderedListPlan>
                        )}
                      </BodySecondary>
                    </StyledPlanDescContainer>
                  </Spacing>
                  <Spacing bottom={4}>
                    <Divider />
                  </Spacing>
                  <Spacing bottom={2}>
                    <Card.Footer>
                      {renderPrice(
                        selectedPlan?.monthlyCharges,
                        selectedPlan?.discountedPrice,
                        selectedPlan?.discountedLiner
                      )}
                    </Card.Footer>
                  </Spacing>
                </Card.Content>
              </Card>
            </Spacing>
          )}
        </StyledPlanSelectionArrowContainer>
        <StyledPhoneSelectionContainer xs={12} sm={8} md={8}>
          <Grid fluid>
            <Spacing left={10} topMd={2} bottom={5}>
              <H3>{t('WANT_DEVICE_WITH_PLAN')}</H3>
            </Spacing>

            <StyledImageListContainer>
              <StyledImageCaptionContainer>
                <StyledImg
                  src={isCis ? ImageSimOnlyCis : ImageSimOnly}
                  alt="Image of Sim Only"
                />
                <StyledSimOnlyImageCaption isCis={isCis}>
                  {t('SIM_ONLY_PLUS__PLAN')}
                </StyledSimOnlyImageCaption>
              </StyledImageCaptionContainer>
              <StyledImgPlus
                src={isCis ? ImagePlusCis : ImagePlus}
                alt="Image of Plus icon"
              />
              <StyledImageCaptionContainer>
                <StyledImg
                  src={isCis ? ImageDeviceCis : ImageDevice}
                  alt="Image of Device"
                />
                <StyledDeviceInstallmentPlanImageCaption isCis={isCis}>
                  {t('DEVICE_INSTALLMENT_PLAN')}
                </StyledDeviceInstallmentPlanImageCaption>
              </StyledImageCaptionContainer>
            </StyledImageListContainer>

            <Spacing top={5} bottom={5}>
              <SubtitleSecondary>
                <StyledDescription isCis={isCis}>
                  {t('SIM_ONLY_PLUS_UPSELL_DESCRIPTION')}
                </StyledDescription>

                <Anchor href={simOnlyTncUrl} target="_blank">
                  {t('T&C')}.
                </Anchor>
              </SubtitleSecondary>
            </Spacing>
            <Spacing left={10} topMd={2} bottom={5}>
              <H3>{t('TOP_CHOICES_FOR_YOU')}</H3>
            </Spacing>
            <Spacing topMd={2} bottom={5}>
              <Carousel
                type="card"
                pagination={2}
                outsideButtons={true}
                infiniteLoop={false}
              >
                {topChoicesPhones.map(phone => {
                  const ribbonForPlan = getRibbon(phone.ribbon, selectedPlan);
                  return (
                    <Card>
                      <Card.Content>
                        {phone.heroImage && (
                          <StyledHeroImage
                            image={
                              phone.heroImage.childImageSharp.gatsbyImageData
                            }
                            alt={phone.title}
                          />
                        )}
                        <Column sm={12}>
                          {ribbonForPlan && (
                            <Ribbon.Variant1>
                              {ribbonForPlan?.title || ''}
                            </Ribbon.Variant1>
                          )}
                          <StyledTitle>{phone.title}</StyledTitle>
                          <SubtitleSecondary style={{ marginBottom: '20px' }}>
                            {setPriceText(phone)}
                          </SubtitleSecondary>
                          <SecondaryButton
                            block
                            secondary
                            onClick={() => navigateProductDetailsPage(phone)}
                          >
                            {t('ADD')}
                          </SecondaryButton>
                        </Column>
                      </Card.Content>
                    </Card>
                  );
                })}
              </Carousel>
            </Spacing>

            <StyledButtonContainer>
              <StyledPrimaryButton
                block
                onClick={showEligibleDevicesForSimOnlyPlus}
              >
                {t('OK_SHOW_ELIGIBLE_DEVICES')}
              </StyledPrimaryButton>
              <StyledSecondaryButton
                block
                secondary
                onClick={proceedSimOnlyPlusWithoutDeviceFlow}
              >
                {t('NO_THANKS')}
              </StyledSecondaryButton>
            </StyledButtonContainer>
            {selectedPlanGroup?.footerLiner && (
              <Spacing top={5} bottom={5}>
                <BodyPrimary>
                  <StyledFooterContent>
                    {htmlStringToReactComponent(selectedPlanGroup.footerLiner)}
                  </StyledFooterContent>
                </BodyPrimary>
              </Spacing>
            )}
          </Grid>
        </StyledPhoneSelectionContainer>
      </Row>
    </StyledMainGridContainer>
  );
};

SimOnlyPlusUpSell.defaultProps = {
  topChoicesLimit: 6
};

export default SimOnlyPlusUpSell;
